import { useState, useEffect, useRef } from 'react';
import api from '../services/api';

const DEFAULT_POLL_INTERVAL = 2000;
const DEFAULT_MAX_POLL_INTERVAL = 10000; // collector jobs (URL scrape) are
// typically fast — capped lower than useProcessingStatus's 30s since there's
// no long-running LLM step in this phase.
const DEFAULT_OVERALL_TIMEOUT = 3 * 60 * 1000; // 3 minutes
const DEFAULT_MAX_RETRIES = 5;

/**
 * Hook to poll a URL-collection job's status (CollectorJob), mirroring
 * useProcessingStatus's polling/backoff/timeout shape (COL-137) but for the
 * collector's flat status — queued|processing|complete|failed, no phase
 * breakdown — and a different id space than a ProcessingJob job_id, so
 * useProcessingStatus cannot be reused as-is.
 *
 * @param {string} jobId - The collector job ID (from POST /collect)
 * @param {object} options
 * @param {number} options.pollInterval - Base polling interval in ms (default: 2000)
 * @param {number} options.maxPollInterval - Cap for backed-off polling interval in ms on errors (default: 10000)
 * @param {boolean} options.enabled - Whether polling is enabled (default: true)
 * @param {number} options.maxRetries - Max consecutive errors before giving up (default: 5)
 * @param {number} options.overallTimeout - Total time in ms to keep polling before giving up (default: 3 minutes)
 * @param {function} options.onComplete - Callback with the CollectorJob when status becomes 'complete'
 * @param {function} options.onError - Callback with the CollectorJob (or a synthetic one) when status becomes 'failed', or polling gives up
 */
export function useCollectStatus(jobId, options = {}) {
  const {
    pollInterval = DEFAULT_POLL_INTERVAL,
    maxPollInterval = DEFAULT_MAX_POLL_INTERVAL,
    enabled = true,
    maxRetries = DEFAULT_MAX_RETRIES,
    overallTimeout = DEFAULT_OVERALL_TIMEOUT,
    onComplete,
    onError,
  } = options;

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Why polling stopped without the job reaching complete/failed: null while
  // still polling, otherwise 'timeout'.
  const [stopReason, setStopReason] = useState(null);

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!jobId || !enabled) {
      setLoading(false);
      return;
    }

    let mounted = true;
    let timeoutId = null;
    let consecutiveErrors = 0;
    const startTime = Date.now();

    setStopReason(null);
    setLoading(true);

    const poll = async () => {
      if (Date.now() - startTime >= overallTimeout) {
        if (mounted) setStopReason('timeout');
        return;
      }

      try {
        const jobData = await api.getCollectStatus(jobId);
        if (!mounted) return;

        consecutiveErrors = 0;
        setJob(jobData);
        setLoading(false);
        setError(null);

        if (jobData.status === 'complete') {
          onCompleteRef.current?.(jobData);
          return; // Stop polling
        }

        if (jobData.status === 'failed') {
          onErrorRef.current?.(jobData);
          return; // Stop polling
        }

        timeoutId = setTimeout(poll, pollInterval);
      } catch (err) {
        if (!mounted) return;

        consecutiveErrors++;
        setError(err.message);
        setLoading(false);

        if (consecutiveErrors >= maxRetries) {
          onErrorRef.current?.({ status: 'failed', error: `Polling failed after ${maxRetries} retries` });
          return;
        }

        const backoffMs = Math.min(pollInterval * Math.pow(2, consecutiveErrors - 1), maxPollInterval);
        timeoutId = setTimeout(poll, backoffMs);
      }
    };

    poll();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [jobId, enabled, pollInterval, maxPollInterval, maxRetries, overallTimeout]);

  return {
    job,
    loading,
    error,
    isComplete: job?.status === 'complete',
    isFailed: job?.status === 'failed',
    // True once polling has given up without the job reaching complete/failed
    // (overall timeout). The caller should tell the user to check back later.
    isTimedOut: stopReason !== null,
    stopReason,
  };
}

export default useCollectStatus;
